import { DEFAULT_FIND_PAGE_SIZE } from "./FindPaging";

/**
 * Single source of truth for the SQL signature of the generated `find_<atom>`
 * functions. Consumed by:
 * - atom-codegen's FindProcedure generator (builds the DDL parameter list)
 * - PrismaPersistenceService.find (binds values with these casts at runtime)
 * - unit-test-kit's callFind/expectFindToThrow (parameterized test calls)
 *
 * Because all three derive from this table, the runtime call, the test
 * helpers, and the generated SQL cannot drift apart: a signature change here
 * regenerates the SQL and retypes every call site in one step, and the
 * testcontainer stored-procedure tests then verify the shape against real
 * Postgres.
 */
export const FIND_CALL_PARAMETERS = [
  { name: "context", type: "json", default: undefined },
  { name: "filter", type: "jsonb", default: "NULL" },
  { name: "sort_column", type: "text", default: "NULL" },
  { name: "sort_direction", type: "text", default: "NULL" },
  { name: "page_number", type: "int", default: "1" },
  { name: "page_size", type: "int", default: `${DEFAULT_FIND_PAGE_SIZE}` },
  { name: "includeAtomData", type: "boolean", default: "FALSE" },
  { name: "fields", type: "jsonb", default: "NULL" },
  { name: "allow_unscoped_context", type: "boolean", default: "FALSE" },
  { name: "includeHistory", type: "boolean", default: "FALSE" },
] as const;

/** Pg type per positional parameter, e.g. `["json", "jsonb", ...]`. */
export const FIND_CALL_CASTS: readonly string[] = FIND_CALL_PARAMETERS.map(
  (p) => p.type,
);

/**
 * DDL parameter list for the generated function, e.g.
 * `context json, filter jsonb DEFAULT NULL, ...`.
 */
export function buildFindSqlSignature(): string {
  return FIND_CALL_PARAMETERS.map((p) => {
    const defaultClause = p.default ? ` DEFAULT ${p.default}` : "";
    return `${p.name} ${p.type}${defaultClause}`;
  }).join(", ");
}

/**
 * Positional placeholder list for parameterized calls, e.g.
 * `$1::json, $2::jsonb, ...`.
 */
export function buildFindPlaceholders(): string {
  return FIND_CALL_PARAMETERS.map((p, i) => `$${i + 1}::${p.type}`).join(", ");
}

/**
 * Reserved key in the `context` JSON of a `find_<atom>` call: the entities the
 * calling transaction has deleted but not committed yet, as an array of
 * `{ relation, context }`. The generated SQL leaves their rows out, so pages
 * stay full and `total` is exact.
 *
 * It is not a context key: only `PrismaPersistenceService.find` writes it, from
 * `findOptions.deletedEntities`, and it drops any value that arrives under this
 * name. SQL generated before the key existed ignores it.
 */
export const FIND_DELETED_ENTITIES_CONTEXT_KEY = "__findDeletedEntities";

/**
 * The most deletions one find call carries. Each one costs a lookup per find,
 * so a longer list is not sent, and the generated SQL ignores one as a whole.
 * The caller's own filter then hides the rows after paging, as it does for
 * every provider without SQL support.
 */
export const FIND_DELETED_ENTITIES_MAX = 100;
